import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {
	type IReactionDisposer,
	makeAutoObservable,
	observable,
	reaction,
	action,
	computed,
} from "mobx";
import {useLocation} from "react-router-dom";

interface IParams {
	isSessionChecked: boolean;
	location: ReturnType<typeof useLocation>;
}

export interface IGlobalModalsController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;

	setIsSessionChecked: (value: boolean) => void;
	onLocationChange: (location: IParams["location"]) => void;
}

@injectable()
export class GlobalModalsController implements IGlobalModalsController {
	@observable private _isSessionChecked = false;
	private _location!: IParams["location"];
	@observable private _userDisposer: IReactionDisposer | null = null;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	@computed
	get isNeedToShowWelcome() {
		return ["/", "/game"].some((path) => this._location.pathname === path);
	}

	@action
	public setIsSessionChecked = (value: boolean) => {
		this._isSessionChecked = value;
	};

	private showWelcomeModal = () => {
		const isPlayedPinpoint = this._userStore.isPlayedPinpoint;

		const isUserViewedWelcome = JSON.parse(
			localStorage.getItem("isUserViewedWelcome") || "false"
		) as boolean;

		if (isUserViewedWelcome || isPlayedPinpoint || !this.isNeedToShowWelcome) return;

		this._modalsStore.showModal(ModalType.WELCOME);

		localStorage.setItem("isUserViewedWelcome", JSON.stringify(true));
	};

	@action
	public onLocationChange = (location: IParams["location"]) => {
		this._location = location;
	};

	dispose() {
		this._userDisposer?.();
	}

	init(param: IParams): void {
		this._isSessionChecked = param.isSessionChecked;
		this._location = param.location;

		this._userDisposer = reaction(
			() => this._isSessionChecked && this.isNeedToShowWelcome,
			() => {
				this.showWelcomeModal();
			}
		);
	}
}
